import { Image as DatoImage, renderMetaTags, TitleMetaLinkTag, useQuerySubscription } from "react-datocms";
import classNames from "classnames";
import { GetStaticProps, GetStaticPaths } from "next";
import { Api } from "@/api";
import { Layout, SEO, Section, Container, BlogAuthor, BlogThreeUp, HtmlToElement } from "@/components";
import { DATOCMS_API_TOKEN } from "@/config";
import {
  metaTagsFragment,
  responsiveImageFragment,
  buttonFragment,
  PromoBannerFragmentType,
  promoBannerFragment,
  PromoBanner,
} from "@/modules";
import { Subscription, IBlogPost } from "@/types";
import { HorizontalRule } from "@narrative-software/narrative-web-ui";

const getTextSection = (key: string, content: string | null, isFirst?: boolean) =>
  content && (
    <Section key={key} className={classNames({ "-mt-25 md:-mt-32 lg:-mt-40 xl:-mt-60": isFirst })}>
      <Container className="flex flex-col items-center">
        <div className="w-full max-w-200">
          <HtmlToElement className="prose text-stealth-bomber">{content}</HtmlToElement>
        </div>
      </Container>
    </Section>
  );

/**
 * Insert a promo banner by splitting the blog content (promo banner indicated in content as a <blockquote>)
 */
const insertBanner = (content: string | null, banner: PromoBannerFragmentType | null) => {
  const openString = "<blockquote>";
  const closeString = "</blockquote>";

  if (!banner || !content?.includes(openString)) {
    return getTextSection("first", content, true);
  }

  const textBlocks = content.split(openString);
  const first = getTextSection("first", textBlocks.shift() || null, true);
  const promoBanner = banner && <PromoBanner {...banner} className="md:-my-4 lg:-my-6 xl:-my-12" />;

  const rest = textBlocks
    .map((string, i) => {
      const block = getTextSection(`block${i}`, string.split(closeString)[1]);
      return [promoBanner, block];
    })
    .flat();

  return [first, rest];
};

interface IBlogPostPage {
  morePosts: IBlogPost[];
  page: IBlogPost & {
    seo: TitleMetaLinkTag[];
  };
}

type Props = {
  preview: boolean;
  subscription: Subscription<IBlogPostPage>;
};

const BlogPostPage: React.FC<Props> = ({ preview, subscription }) => {
  const { data } = useQuerySubscription(subscription);
  if (!data) return null;

  const {
    morePosts,
    page: { seo, title, date, content, publishBlogPostHtml, banner, isCoverImage, image, author },
  } = data;

  const mainContent = insertBanner(publishBlogPostHtml || content, banner);

  return (
    <Layout preview={preview}>
      <SEO title={`${title} | Narrative`}>{renderMetaTags(seo)}</SEO>

      <Section isFirstSection>
        <Container className="flex flex-col items-center">
          <h1 className="mb-8 text-h2 font-semibold text-center leading-tight lg:text-h1">{title}</h1>
          <HorizontalRule bottomMargin />

          {image && isCoverImage ? (
            <div className="mt-5">
              <DatoImage data={image.responsiveImage} />
            </div>
          ) : null}

          {author && (
            <div className="w-full max-w-200 my-8">
              <BlogAuthor name={author.name} date={date} image={author.avatar} />
            </div>
          )}
        </Container>
      </Section>

      {mainContent}

      {morePosts.length && <BlogThreeUp title="You may also like" items={morePosts} />}
    </Layout>
  );
};

/**
 * Static paths
 */
export const getStaticPaths: GetStaticPaths = async () => {
  // First, get the total count of blog posts
  const {
    data: {
      data: {
        _allPageBlogPostsMeta: { count },
      },
    },
  } = await Api.datocmsGraphqlRequest<{ _allPageBlogPostsMeta: { count: number } }>({
    query: `
      {
        _allPageBlogPostsMeta {
          count
        }
      }
    `,
  });

  const POSTS_PER_BATCH = 50;
  const DELAY_BETWEEN_REQUESTS_MS = 2000;
  const totalPosts = count;
  const batchCount = Math.ceil(totalPosts / POSTS_PER_BATCH);

  // Fetch all posts in batches
  const allPosts: { slug: string }[] = [];

  for (let i = 0; i < batchCount; i++) {
    const skip = i * POSTS_PER_BATCH;
    const {
      data: {
        data: { pages },
      },
    } = await Api.datocmsGraphqlRequest<{ pages: { slug: string }[] }>({
      query: `
        query($first: IntType!, $skip: IntType!) {
          pages: allPageBlogPosts(first: $first, skip: $skip) {
            slug
          }
        }
      `,
      variables: {
        first: POSTS_PER_BATCH.toString(),
        skip: skip.toString(),
      },
    });

    allPosts.push(...pages);

    // Add delay between requests to avoid rate limiting (skip delay on last batch)
    if (i < batchCount - 1) {
      await new Promise((resolve) => setTimeout(resolve, DELAY_BETWEEN_REQUESTS_MS));
    }
  }

  return {
    paths: allPosts.map(({ slug }) => ({ params: { slug } })),
    fallback: false,
  };
};

/**
 * Static props
 */
export const getStaticProps: GetStaticProps<Props> = async ({ params, preview = false }) => {
  console.log("getStaticProps");
  console.log("slug: ", params?.slug);

  const graphqlRequest = {
    preview,
    variables: {
      slug: params?.slug,
    },
    query: `
      query($slug: String) {
        page: pageBlogPost(filter: { slug: { eq: $slug }}) {
          id
          title
          slug
          date
          content
          publishBlogPostHtml
          isCoverImage
          banner {
            ...promoBannerFragment
          }
          author {
            name
            avatar {
              responsiveImage(imgixParams: { auto: format, fit: crop, w: 144, h: 144 }) {
                ...responsiveImageFragment
              }
            }
          }
          image {
            responsiveImage(imgixParams: { auto: format, fit: crop, w: 2432, h: 1000 }) {
              ...responsiveImageFragment
            }
          }
          seo: _seoMetaTags {
            ...metaTagsFragment
          }
        }

        morePosts: allPageBlogPosts(
          first: 3,
          orderBy: date_DESC,
          filter: { slug: { neq: $slug }}
        ) {
          id
          title
          slug
          parentPage {
            slug
          }
          category {
            name
          }
          image {
            responsiveImage(imgixParams: { auto: format, fit: crop, w: 1200, h: 800 }) {
              ...responsiveImageFragment
            }
          }
        }
      }
      ${metaTagsFragment}
      ${responsiveImageFragment}
      ${buttonFragment}
      ${promoBannerFragment}
    `,
  };

  const {
    data: { data: initialData },
  } = await Api.datocmsGraphqlRequest<IBlogPostPage>(graphqlRequest);

  // If the page doesn’t exist, throw 404
  if (!initialData?.page) {
    return { notFound: true };
  }

  return {
    props: {
      preview,
      subscription: preview
        ? {
            ...graphqlRequest,
            initialData,
            token: DATOCMS_API_TOKEN,
          }
        : {
            initialData,
            enabled: false,
          },
    },
  };
};

export default BlogPostPage;
